import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_VI } from "material-react-table/locales/vi";
import {
  fetchClients,
  fetchContractors,
  // fetchDocumentTypes,
  getDocumentTypes,
  createContract,
  updateContract,
  uploadContractDocument,
  getContractDocumentForPreview,
  addContractTeamMember,
  getContract,
  getContractTeamMembers,
  getContractDocuments,
  removeContractTeamMember,
  updateContractTeamMember,
  deleteContractDocument,
  downloadContractDocument,
  updateContractStatus,
} from "../../services/contract.service";
import { fetchPersonnel } from "modules/systems/services/personnel/personnel.service";
import { fetchContractGroups } from "../../services/contractGroup.service";
import FileDropzone from "./FileDropzone";
import Checkbox from "@mui/material/Checkbox";
import api from "services/api";
import { openDocumentPreviewInNewTab } from "utils/documentPreview";
import { AppButton } from "components/common/buttons";
import { AppTab, AppTabPanel } from "components/common/tabs";
import { AppAutocomplete, AppSelect, AppTextField } from "components/common/inputs";
import DataTable from "components/common/tables/DataTable";
import {
  DeleteOutline, DownloadOutlined, VisibilityOutlined,
} from "@mui/icons-material";

const CONTRACT_STATUSES = [
  "Chưa bắt đầu",
  "Đang thực hiện",
  "Hoàn thành",
  "Tạm dừng",
  "Hủy bỏ",
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
    </div>
  );
}

const AddContractModal = ({
  open,
  onClose,
  onContractAdded,
  editMode = false,
  contractId = null,
}) => {
  // Store contract ID in a ref to avoid issues with stale closures
  const contractIdRef = React.useRef(contractId);

  // Update ref when prop changes
  React.useEffect(() => {
    contractIdRef.current = contractId;
  }, [contractId]);
  const [form, setForm] = useState({
    contractNumber: "",
    contractName: "",
    clientId: "",
    contractorId: "",
    signDate: "",
    startDate: "",
    endDate: "",
    startDateAct: "",
    endDateAct: "",
    totalValue: "",
    description: "",
    status: CONTRACT_STATUSES[0],
    currency: "VND",
    vat: 8, // Default 8%
    contractFilePath: null,
    scope: "",
    contractGroup: "",
  });

  const [isEditMode] = useState(editMode);
  const [clients, setClients] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [contractGroups, setContractGroups] = useState([]);
  const [selectedPersonnels, setSelectedPersonnels] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docUpload, setDocUpload] = useState({
    file: null,
    documentType: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [personnelSearch, setPersonnelSearch] = useState("");

  // State for documents pagination
  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentsPerPage, setDocumentsPerPage] = useState(5); // 5 documents per page

  // State cho preview file
  const [previewModal, setPreviewModal] = useState({
    open: false,
    file: null,
    fileName: "",
    fileType: "",
    fileBlob: null,
  });

  const tabs = [
    { label: "Thông tin hợp đồng", value: 0 },
    { label: "Đính kèm", value: 1 },
    { label: "Nhân sự theo hợp đồng", value: 2 },
  ];

  // Paginated documents for current page
  const paginatedDocuments = useMemo(() => {
    const startIndex = (documentsPage - 1) * documentsPerPage;
    const endIndex = startIndex + documentsPerPage;
    return documents.slice(startIndex, endIndex);
  }, [documents, documentsPage, documentsPerPage]);

  // Total pages for documents
  const totalDocumentPages = Math.ceil(documents.length / documentsPerPage);

  // Function to reload documents from the server
  const reloadDocuments = React.useCallback(async () => {
    if (!contractIdRef.current || !isEditMode) return;

    try {
      setLoading(true);

      const docs = await getContractDocuments(contractIdRef.current);

      // Get only documents that are truly unsaved (have a File object but no server ID)
      // These are files that may have been added to the UI but not yet uploaded to server
      const unsavedDocuments = documents.filter(
        (doc) => !doc.id && doc.file instanceof File
      );

      if (Array.isArray(docs)) {
        // Transform to match the docUpload structure
        const formattedDocs = docs.map((doc) => ({
          id: doc.documentId,
          file: { name: doc.documentName || doc.fileName || "Tài liệu" },
          fileName: doc.documentName || doc.fileName || "Tài liệu",
          documentType: doc.documentType,
          description: doc.description || "",
          filePath: doc.filePath,
          // Important: mark server documents as not new
          isNew: false,
        }));

        // Only combine with truly unsaved documents
        if (unsavedDocuments.length > 0) {
          setDocuments([...formattedDocs, ...unsavedDocuments]);
        } else {
          setDocuments(formattedDocs);
        }
      } else {
        console.warn("DEBUG: Manual reload: No documents or invalid format");
        // Keep any unsaved documents if we couldn't get the server list
        setDocuments(unsavedDocuments);
      }
    } catch (error) {
      console.error("DEBUG: Error manually reloading documents:", error);
    } finally {
      setLoading(false);
    }
  }, [isEditMode, documents]);
  const [personnelPage, setPersonnelPage] = useState(0);
  const [personnelPageSize, setPersonnelPageSize] = useState(10);
  const [personnelTotal, setPersonnelTotal] = useState(0);
  const [personnelLoading, setPersonnelLoading] = useState(false);
  const [personnels, setPersonnels] = useState([]);
  const [selectedPersonnelIds, setSelectedPersonnelIds] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [personnelRoles, setPersonnelRoles] = useState({}); // { personnelId: roleName }
  const [personnelDisplayOrders, setPersonnelDisplayOrders] = useState({}); // { personnelId: displayOrder }

  // Lưu trữ state của các tab
  const [tabsInitialized, setTabsInitialized] = useState({
    0: false, // Thông tin hợp đồng
    1: false, // Đính kèm
    2: false, // Thành viên
  });

  // New state variables for status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [initialStatus, setInitialStatus] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [statusChangeReason, setStatusChangeReason] = useState("");
  const [statusChangeError, setStatusChangeError] = useState("");

  // State for selected personnel modal
  const [selectedPersonnelModalOpen, setSelectedPersonnelModalOpen] =
    useState(false);

  useEffect(() => {
    if (open) {
      // Set loading state
      setLoading(true);

      // Reset tabs initialization state
      setTabsInitialized({
        0: false, // Thông tin hợp đồng
        1: false, // Đính kèm
        2: false, // Thành viên
      });

      // Reset form when opening in create mode
      if (!isEditMode) {
        setForm({
          contractNumber: "",
          contractName: "",
          clientId: "",
          contractorId: "",
          signDate: "",
          startDate: "",
          endDate: "",
          startDateAct: "",
          endDateAct: "",
          totalValue: "",
          description: "",
          status: CONTRACT_STATUSES[0],
          currency: "VND",
          vat: 8, // Default 8%
          contractFilePath: null,
          scope: "",
          contractGroup: "",
        });
        setDocuments([]);
        setSelectedPersonnels([]);
        setSelectedPersonnelIds([]);
        setLoading(false); // No need to load data in create mode
      }

      // Fetch common data for both create and edit modes
      fetchClients().then(setClients);
      fetchContractors().then(setContractors);
      // fetchDocumentTypes().then(setDocumentTypes);
      getDocumentTypes({
        documentCategory: "Hợp đồng",
        pageSize: 1000,
        pageNumber: 1,
        sortColumn: "DocumentCategory",
        sortDirection: "ASC",
      }).then(setDocumentTypes);

      // Lấy danh sách vai trò trong hợp đồng
      api
        .get("/RoleInContract", {
          params: { pageSize: 1000, pageNumber: 1, sortDirection: "ASC" },
        })
        .then((res) => {
          if (res.data && Array.isArray(res.data.data)) {
            setRoleOptions(res.data.data.map((r) => r.name));
          }
        })
        .catch((err) => {
          setRoleOptions(["Thành viên"]); // fallback nếu lỗi
        });

      // If in edit mode, fetch contract data
      if (isEditMode && contractIdRef.current) {
        const currentContractId = contractIdRef.current;

        // Create an array to track which API calls have completed
        const apiCalls = { contract: false, members: false, documents: false };
        const checkAllComplete = () => {
          if (apiCalls.contract && apiCalls.members && apiCalls.documents) {
            setLoading(false);
          }
        };

        // Fetch contract details
        getContract(currentContractId)
          .then((response) => {
            if (response && response.data) {
              const contract = response.data;
              // Format dates for form inputs
              const status = contract.status || CONTRACT_STATUSES[0];
              setForm({
                contractNumber: contract.contractNumber || "",
                contractName: contract.contractName || "",
                clientId: contract.clientId || "",
                contractorId: contract.contractorId || "",
                signDate: contract.signDate
                  ? contract.signDate.split("T")[0]
                  : "",
                startDate: contract.startDate
                  ? contract.startDate.split("T")[0]
                  : "",
                endDate: contract.endDate ? contract.endDate.split("T")[0] : "",
                startDateAct: contract.startDateAct
                  ? contract.startDateAct.split("T")[0]
                  : "",
                endDateAct: contract.endDateAct
                  ? contract.endDateAct.split("T")[0]
                  : "",
                totalValue: formatNumber(contract.totalValue || ""),
                description: contract.description || "",
                status: status,
                currency: contract.currency || "VND",
                vat: contract.vat || 8, // Default 8% if not set
                contractFilePath: contract.contractFilePath || null,
                scope: contract.scope || "",
                contractGroup: contract.contractGroup || "",
              });

              // Also set the initial status for tracking changes
              setInitialStatus(status);
            }
            apiCalls.contract = true;
            checkAllComplete();
          })
          .catch((error) => {
            setSnackbar({
              open: true,
              message: "Không thể tải thông tin hợp đồng. Vui lòng thử lại.",
              severity: "error",
            });
            apiCalls.contract = true;
            checkAllComplete();
          });

        // Fetch team members
        getContractTeamMembers(currentContractId)
          .then((members) => {
            if (Array.isArray(members)) {
              // Map team members to their IDs, considering userId field in API might map to personnelId
              const memberIds = members.map((m) => m.userId || m.personnelId);
              setSelectedPersonnelIds(memberIds);

              // Set role for each member
              const roles = {};
              const displayOrders = {};
              members.forEach((m) => {
                // Use userId or personnelId depending on API response
                const personnelId = m.userId || m.personnelId;
                roles[personnelId] = m.roleInContract || "Thành viên";
                displayOrders[personnelId] = m.displayOrder || 0;
              });
              setPersonnelRoles(roles);
              setPersonnelDisplayOrders(displayOrders);
            }
            apiCalls.members = true;
            checkAllComplete();
          })
          .catch((error) => {
            console.error("Error loading team members:", error);
            setSnackbar({
              open: true,
              message:
                "Không thể tải danh sách thành viên dự án. Vui lòng thử lại.",
              severity: "error",
            });
            apiCalls.members = true;
            checkAllComplete();
          });

        // Fetch documents

        getContractDocuments(currentContractId)
          .then((docs) => {
            if (Array.isArray(docs)) {
              // Transform to match the docUpload structure
              const formattedDocs = docs.map((doc) => ({
                id: doc.documentId,
                file: { name: doc.documentName }, // Changed from fileName to documentName to match backend model
                documentType: doc.documentType,
                description: doc.description || "",
                filePath: doc.filePath,
              }));

              setDocuments(formattedDocs);
            } else {
              console.warn("No documents returned or invalid format:", docs);
              setDocuments([]);
            }
            apiCalls.documents = true;
            checkAllComplete();
          })
          .catch((error) => {
            console.error("Error loading documents:", error);
            console.error("Error details:", error.response || error.message);
            setDocuments([]);
            apiCalls.documents = true;
            checkAllComplete();
          });
      }
    }
  }, [open, isEditMode, contractId]);

  // Load contract groups for dropdown
  useEffect(() => {
    const loadContractGroups = async () => {
      try {
        const groups = await fetchContractGroups();
        if (Array.isArray(groups)) {
          setContractGroups(groups);
        }
      } catch (error) {
        console.error("Error loading contract groups:", error);
        setContractGroups([]);
      }
    };

    loadContractGroups();
  }, []);

  // Fetch only active personnel with search and pagination
  useEffect(() => {
    if (tab !== 2) return;

    // Mark personnel tab as initialized when user opens it
    if (!tabsInitialized[2]) {
      setTabsInitialized((prev) => ({ ...prev, 2: true }));
    }

    setPersonnelLoading(true);
    fetchPersonnel(
      personnelPageSize,
      personnelPage + 1,
      personnelSearch,
      "displayOrder", // sắp xếp theo displayOrder để hiển thị theo thứ tự tùy chỉnh
      "ASC", // sort tăng dần theo displayOrder
      true // isActive = true - chỉ lấy nhân sự đang hoạt động
    )
      .then((res) => {
        // Đã lọc từ server, chỉ cần kiểm tra và set dữ liệu
        const activePersonnels = Array.isArray(res.items) ? res.items : [];

        setPersonnels(activePersonnels);
        setPersonnelTotal(res.totalItems || 0); // Số lượng này đã là của nhân sự active
        setPersonnelLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching personnel:", error);
        setPersonnelLoading(false);
      });
  }, [
    tab,
    personnelPage,
    personnelPageSize,
    personnelSearch,
    selectedPersonnelIds,
    tabsInitialized,
  ]);

  // Khi thay đổi selectedPersonnelIds, cập nhật selectedPersonnels và personnelRoles
  useEffect(() => {
    const selected = personnels.filter((p) =>
      selectedPersonnelIds.includes(p.personnelId)
    );
    setSelectedPersonnels(
      selected.map((p) => ({
        ...p,
        roleInContract: personnelRoles[p.personnelId] || "Thành viên",
      }))
    );
  }, [selectedPersonnelIds, personnels, personnelRoles]);

  // Reload documents when switching to the attachments tab - only first time
  useEffect(() => {
    // Chỉ reload documents từ server nếu là lần đầu vào tab đính kèm trong chế độ chỉnh sửa
    if (
      tab === 1 &&
      isEditMode &&
      contractIdRef.current &&
      !tabsInitialized[1]
    ) {
      reloadDocuments();
      // Đánh dấu tab này đã được khởi tạo
      setTabsInitialized((prev) => ({ ...prev, 1: true }));
    } else if (tab === 1) {
      // Khi chuyển sang tab đính kèm, không reload từ server nhưng vẫn log để theo dõi

      documents.forEach((doc, index) => {
        if (doc.isNew || (!doc.id && doc.file)) {
        } else {
        }
      });
    }
  }, [
    tab,
    isEditMode,
    reloadDocuments,
    tabsInitialized,
    contractIdRef,
    documents,
  ]);

  // Hàm format số theo chuẩn Việt Nam (dấu "." phân tách hàng nghìn, dấu "," phân tách thập phân)
  const formatNumber = (value) => {
    if (!value) return "";

    // Xóa tất cả ký tự không phải số, dấu "," và "."
    const cleanValue = value.toString().replace(/[^\d,.]/g, "");

    // Tách phần nguyên và phần thập phân (tìm dấu "," cuối cùng làm phân tách thập phân)
    const lastCommaIndex = cleanValue.lastIndexOf(",");
    let integerPart, decimalPart;

    if (lastCommaIndex !== -1) {
      integerPart = cleanValue
        .substring(0, lastCommaIndex)
        .replace(/[,.]/g, ""); // Xóa tất cả dấu "," và "."
      decimalPart = cleanValue.substring(lastCommaIndex + 1);
    } else {
      integerPart = cleanValue.replace(/[,.]/g, ""); // Xóa tất cả dấu "," và "."
      decimalPart = "";
    }

    // Format phần nguyên với dấu "." phân tách hàng nghìn
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return formattedInteger + (decimalPart ? "," + decimalPart : "");
  };

  // Hàm unformat số (chuyển về dạng số thuần túy với dấu "." làm phân tách thập phân)
  const unformatNumber = (value) => {
    if (!value) return "";
    // Thay thế dấu "." phân tách nghìn bằng rỗng, và dấu "," thập phân thành "."
    return value.toString().replace(/\./g, "").replace(/,/g, ".");
  }; // Hàm xử lý thay đổi cho trường totalValue
  const handleTotalValueChange = (e) => {
    const { value } = e.target;
    const formattedValue = formatNumber(value);

    setForm((prev) => ({ ...prev, totalValue: formattedValue }));
    setErrors((prev) => ({ ...prev, totalValue: undefined }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If changing status and in edit mode, open the status change dialog
    if (name === "status" && isEditMode) {
      // Store the initial status value before changes
      setInitialStatus(form.status);
      setNewStatus(value);
      setStatusChangeReason("");
      setStatusChangeError("");
      setStatusDialogOpen(true);
      return; // Don't update the form yet
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleDateChange = (name, dateStr) => {
    setForm((prev) => {
      const nextForm = { ...prev, [name]: dateStr };

      // Validate logic ngày bắt đầu / kết thúc
      const startDate = name === "startDate" ? dateStr : nextForm.startDate;
      const endDate = name === "endDate" ? dateStr : nextForm.endDate;

      if (startDate && endDate) {
        if (new Date(startDate) >= new Date(endDate)) {
          setErrors((prevErr) => ({
            ...prevErr,
            endDate: "Ngày kết thúc phải sau ngày bắt đầu",
          }));
        } else {
          setErrors((prevErr) => ({ ...prevErr, endDate: undefined }));
        }
      }

      return nextForm;
    });
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };


  // Các hàm xử lý tài liệu
  const handleDocTypeChange = (e) => {
    setDocUpload((prev) => ({ ...prev, documentType: e.target.value }));
  };
  const handleDocDescChange = (e) => {
    setDocUpload((prev) => ({ ...prev, description: e.target.value }));
  };
  const handleAddDocument = async () => {
    if (!docUpload.file || !docUpload.documentType) {
      setAlert({ type: "error", message: "Chọn file và loại tài liệu!" });
      return;
    }

    // Validate that file is a proper File object
    if (!(docUpload.file instanceof File)) {
      setAlert({
        type: "error",
        message: "File không hợp lệ, vui lòng chọn lại!",
      });
      return;
    }

    // Lưu tên file để hiển thị trong thông báo
    const fileName = docUpload.file.name;
    const fileSize = docUpload.file.size;

    // Add document to the list - lưu đầy đủ thông tin về file
    const newDocument = {
      id: null, // Chưa lưu trên server
      file: docUpload.file, // Lưu trực tiếp đối tượng File
      fileName: fileName, // Thêm trường này để dễ dàng hiển thị
      documentType: docUpload.documentType || "",
      description: docUpload.description || "",
      isNew: true, // Đánh dấu là tài liệu mới thêm - CỰC KỲ QUAN TRỌNG
    };

    // Nếu đang ở chế độ edit và đã có ID hợp đồng, upload ngay tài liệu
    if (isEditMode && contractIdRef.current) {
      setLoading(true); // Hiển thị trạng thái loading

      try {
        // Thêm vào UI trước để người dùng thấy ngay
        setDocuments((prev) => [...prev, newDocument]);

        // Upload lên server
        const result = await uploadDocumentToServer(
          newDocument,
          contractIdRef.current
        );

        // Làm mới danh sách tài liệu từ server sau khi upload
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Chờ 1 giây
        await reloadDocuments();

        // Thông báo thành công
        setSnackbar({
          open: true,
          message: `Đã tải lên và lưu tài liệu ${fileName} thành công`,
          severity: "success",
        });
      } catch (error) {
        console.error("DEBUG: Error uploading document in edit mode:", error);
        setSnackbar({
          open: true,
          message: `Lỗi khi tải lên tài liệu: ${error.message || "Lỗi không xác định"
            }`,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    } else {
      // Chế độ tạo mới: chỉ thêm vào danh sách UI, sẽ upload sau khi lưu hợp đồng
      setDocuments((prev) => {
        const updatedDocs = [...prev, newDocument];

        return updatedDocs;
      });

      // Thông báo thành công (chỉ thêm vào danh sách)
      setSnackbar({
        open: true,
        message: `Đã thêm tài liệu ${fileName} vào danh sách tài liệu đính kèm`,
        severity: "success",
      });
    }

    // Reset upload form trong mọi trường hợp
    setDocUpload({ file: null, documentType: "", description: "" });
    setAlert(null);
  };

  // Function để xem trước file
  const handlePreviewDocument = async (doc, isSavedDoc = false) => {
    const previewData = await openDocumentPreviewInNewTab({
      file: doc,
      isRemote: isSavedDoc,
      downloadFn: getContractDocumentForPreview,
      onAlert: setSnackbar,
    });
  };

  const validate = () => {
    const newErrors = {};
    const { signDate, startDate, endDate, startDateAct, endDateAct } = form;
    // Ngày bắt đầu hợp đồng nhỏ hơn ngày ký
    if (signDate && startDate && new Date(startDate) < new Date(signDate)) {
      newErrors.startDate = "Ngày bắt đầu hợp đồng không được nhỏ hơn ngày ký";
    }
    // Ngày kết thúc hợp đồng bằng hoặc nhỏ hơn ngày bắt đầu
    if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
      newErrors.endDate = "Ngày kết thúc hợp đồng phải lớn hơn ngày bắt đầu";
    }
    // Ngày bắt đầu thực tế nhỏ hơn ngày ký
    if (
      signDate &&
      startDateAct &&
      new Date(startDateAct) < new Date(signDate)
    ) {
      newErrors.startDateAct =
        "Ngày bắt đầu thực tế không được nhỏ hơn ngày ký";
    }
    // Ngày kết thúc thực tế nhỏ hơn ngày bắt đầu thực tế
    if (
      startDateAct &&
      endDateAct &&
      new Date(endDateAct) < new Date(startDateAct)
    ) {
      newErrors.endDateAct =
        "Ngày kết thúc thực tế không được nhỏ hơn ngày bắt đầu thực tế";
    }
    // Các validate cũ
    if (!form.contractNumber) newErrors.contractNumber = "Bắt buộc";
    if (!form.contractName) newErrors.contractName = "Bắt buộc";
    if (!form.clientId) newErrors.clientId = "Bắt buộc";
    if (!form.contractorId) newErrors.contractorId = "Bắt buộc";
    if (!form.signDate) newErrors.signDate = "Bắt buộc";
    if (!form.startDate) newErrors.startDate = "Bắt buộc";
    if (!form.endDate) newErrors.endDate = "Bắt buộc";
    if (!form.totalValue) {
      newErrors.totalValue = "Bắt buộc";
    } else {
      // Kiểm tra giá trị sau khi unformat có phải là số hợp lệ không
      const unformattedValue = unformatNumber(form.totalValue);
      if (isNaN(Number(unformattedValue)) || Number(unformattedValue) <= 0) {
        newErrors.totalValue = "Giá trị phải là số dương hợp lệ";
      }
    }
    if (!form.status) newErrors.status = "Bắt buộc";
    if (!form.currency) newErrors.currency = "Bắt buộc";

    // Validation cho VAT
    if (form.vat !== null && form.vat !== undefined && form.vat !== "") {
      const vatValue = Number(form.vat);
      if (isNaN(vatValue) || vatValue < 0 || vatValue > 100) {
        newErrors.vat = "VAT phải là số từ 0 đến 100";
      }
    }

    // Validation cho DisplayOrder của personnel
    if (selectedPersonnelIds.length > 0) {
      const displayOrderValues = selectedPersonnelIds
        .map((id) => personnelDisplayOrders[id])
        .filter(
          (value) => value !== undefined && value !== null && value !== ""
        )
        .map((value) => parseInt(value));

      // Kiểm tra giá trị âm
      const negativeValues = displayOrderValues.filter((value) => value < 0);
      if (negativeValues.length > 0) {
        newErrors.displayOrder = "Thứ tự hiển thị không được nhỏ hơn 0";
      }

      // Kiểm tra trùng lặp thứ tự
      const duplicateValues = displayOrderValues.filter(
        (value, index) => displayOrderValues.indexOf(value) !== index
      );
      if (duplicateValues.length > 0) {
        newErrors.displayOrder = `Thứ tự hiển thị bị trùng lặp: ${[
          ...new Set(duplicateValues),
        ].join(", ")}`;
      }
    }

    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // Hiển thị lỗi đầu tiên lên snackbar
      const firstError = Object.values(newErrors)[0];
      setSnackbar({ open: true, message: firstError, severity: "error" });
      return;
    }
    setSubmitting(true);
    setAlert(null);
    try {
      // Xử lý startDateAct và endDateAct: nếu là chuỗi rỗng thì chuyển thành null
      const payload = {
        ...form,
        totalValue: unformatNumber(form.totalValue), // Chuyển về giá trị số không có format
        startDateAct: form.startDateAct ? form.startDateAct : null,
        endDateAct: form.endDateAct ? form.endDateAct : null,
        parentContractId: null,
        contractFilePath: form.contractFilePath,
      };

      // Nếu đang ở chế độ edit và có thay đổi trạng thái, thì xóa trạng thái khỏi payload
      // để tránh xung đột với API cập nhật trạng thái riêng
      if (isEditMode && initialStatus !== form.status && statusChangeReason) {
        // Xóa trạng thái khỏi payload để không cập nhật trong API PUT
        delete payload.status;
      }

      let contractId;
      let contractRes;

      if (isEditMode && contractIdRef.current) {
        const currentContractId = contractIdRef.current;
        // Update existing contract
        contractRes = await updateContract(currentContractId, payload);
        if (!contractRes.success)
          throw new Error(contractRes.message || "Cập nhật hợp đồng thất bại");

        // If status was changed, send status update API call
        if (statusChangeReason && initialStatus !== form.status) {
          try {
            await updateContractStatus(currentContractId, {
              status: form.status,
              changeReason: statusChangeReason,
            });
          } catch (error) {
            console.error("Error updating status history:", error);
            // We don't want to fail the entire update if just the status history fails
            setSnackbar({
              open: true,
              message:
                "Đã lưu hợp đồng nhưng không thể cập nhật lịch sử trạng thái",
              severity: "warning",
            });
          }
        }

        // Upload new documents in edit mode

        // Track if any documents were uploaded
        let documentsUploaded = false;

        // Find all documents that need to be uploaded
        const documentsToUpload = documents.filter(
          (doc) =>
            doc.isNew === true ||
            (!doc.filePath && doc.file instanceof File) ||
            (doc.file instanceof File && !doc.id)
        );

        // Upload each document one at a time
        for (const doc of documentsToUpload) {
          try {
            const result = await uploadDocumentToServer(doc, currentContractId);

            documentsUploaded = true;

            // Nếu upload thành công, chờ 1 giây để đảm bảo backend đã xử lý xong
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Refresh document list after each successful upload
            // This ensures the UI shows the most recent state
            try {
              await reloadDocuments();
            } catch (refreshError) {
              console.error(
                "DEBUG: Error refreshing document list after upload:",
                refreshError
              );
            }
          } catch (uploadError) {
            console.error("DEBUG: Failed to upload document:", uploadError);
            // Don't stop the update process if one document fails to upload
            setSnackbar({
              open: true,
              message: `Không thể tải lên tài liệu ${doc.file?.name || "không xác định"
                }: ${uploadError.message || "Lỗi không xác định"}`,
              severity: "warning",
            });
          }
        }

        // For documents that don't need upload, log them
        const skippedDocs = documents.filter(
          (doc) =>
            !(
              doc.isNew === true ||
              (!doc.filePath && doc.file instanceof File) ||
              (doc.file instanceof File && !doc.id)
            )
        );

        // Final document refresh if any were uploaded
        if (documentsUploaded) {
          try {
            await reloadDocuments();
          } catch (refreshError) {
            console.error(
              "DEBUG: Error on final document refresh:",
              refreshError
            );
          }
        }

        // Only update personnel if the personnel tab was initialized (user opened it)
        // This prevents accidentally clearing personnel data when user only updates contract info
        if (tabsInitialized[2]) {
          // For edit mode, we'll first get existing team members
          const existingTeamMembers = await getContractTeamMembers(
            currentContractId
          );

          // Map team member IDs to their database IDs for easy lookup
          const teamMemberMap = {};
          existingTeamMembers.forEach((member) => {
            teamMemberMap[member.personnelId] = member.teamMemberId;
          });

          const existingPersonnelIds = existingTeamMembers.map(
            (m) => m.personnelId
          );
          const selectedPersonnelIds = selectedPersonnels.map(
            (p) => p.personnelId
          );

          // Add new team members
          for (const personnel of selectedPersonnels) {
            // If this personnel is not already in the team
            if (!existingPersonnelIds.includes(personnel.personnelId)) {
              await addContractTeamMember({
                contractId: currentContractId,
                userId: personnel.personnelId, // Lưu personnelId vào trường userId của ContractTeam
                roleInContract: personnel.roleInContract || "Thành viên",
                displayOrder:
                  parseInt(personnelDisplayOrders[personnel.personnelId]) || 0,
                joinDate: form.signDate,
                endDate: null,
                isActive: true,
                notes: "",
              });
            } else {
              // Update existing member's role if needed
              const existingMember = existingTeamMembers.find(
                (m) =>
                  m.personnelId === personnel.personnelId ||
                  m.userId === personnel.personnelId
              );
              if (
                existingMember &&
                existingMember.roleInContract !==
                (personnelRoles[personnel.personnelId] || "Thành viên")
              ) {
                await updateContractTeamMember(
                  existingMember.teamMemberId || existingMember.contractTeamId,
                  {
                    contractId: currentContractId,
                    userId: personnel.personnelId,
                    roleInContract:
                      personnelRoles[personnel.personnelId] || "Thành viên",
                    displayOrder:
                      parseInt(personnelDisplayOrders[personnel.personnelId]) ||
                      existingMember.displayOrder ||
                      0,
                    joinDate: existingMember.joinDate || form.signDate,
                    endDate: existingMember.endDate || null,
                    isActive:
                      existingMember.isActive !== undefined
                        ? existingMember.isActive
                        : true,
                    notes: existingMember.notes || "",
                  }
                );
              }
            }
          }

          // Remove team members that are no longer selected
          for (const member of existingTeamMembers) {
            // Check against both userId and personnelId since backend mapping may vary
            const memberId = member.userId || member.personnelId;
            if (!selectedPersonnelIds.includes(memberId)) {
              await removeContractTeamMember(
                member.teamMemberId || member.contractTeamId
              );
            }
          }
        } else {
        }

        // Handle documents - compare existing and new
        const existingDocuments = await getContractDocuments(currentContractId);

        const existingDocIds = existingDocuments.map((doc) => doc.documentId);

        // Get IDs of documents that we still have in our documents array
        const keptDocumentIds = documents
          .filter((doc) => doc.id)
          .map((doc) => doc.id);

        // Delete documents that are no longer in our list
        for (const docId of existingDocIds) {
          if (!keptDocumentIds.includes(docId)) {
            await deleteContractDocument(docId);
          }
        }
      } else {
        // Create new contract
        contractRes = await createContract(payload);
        if (!contractRes.success)
          throw new Error(contractRes.message || "Tạo hợp đồng thất bại");

        contractId = contractRes.data.contractId;

        // Upload documents for new contract
        for (const doc of documents) {
          try {
            await uploadDocumentToServer(doc, contractId);
          } catch (uploadError) {
            console.error(
              "Failed to upload document for new contract:",
              uploadError
            );
            // Không làm thất bại việc tạo hợp đồng nếu tải lên tài liệu thất bại
            setSnackbar({
              open: true,
              message: `Không thể tải lên tài liệu ${doc.file?.name || "không xác định"
                }: ${uploadError.message || "Lỗi không xác định"}`,
              severity: "warning",
            });
          }
        }

        // Add team members
        for (const personnel of selectedPersonnels) {
          await addContractTeamMember({
            contractId: contractId,
            userId: personnel.personnelId, // Lưu personnelId vào trường userId của ContractTeam
            personnelId: personnel.personnelId, // Added for clarity in our code
            roleInContract:
              personnelRoles[personnel.personnelId] || "Thành viên", // Get role from personnelRoles
            displayOrder:
              parseInt(personnelDisplayOrders[personnel.personnelId]) || 0,
            joinDate: form.signDate,
            endDate: null,
            isActive: true,
            notes: "",
          });
        }
      }

      setAlert({
        type: "success",
        message: isEditMode
          ? "Cập nhật hợp đồng thành công!"
          : "Tạo hợp đồng thành công!",
      });
      if (onContractAdded) onContractAdded();
      onClose();
    } catch (err) {
      setAlert({ type: "error", message: err.message || "Có lỗi xảy ra" });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePersonnelCheck = useCallback((personnelId) => {
    setSelectedPersonnelIds((prev) => {
      if (prev.includes(personnelId)) {
        // Nếu bỏ chọn, xóa khỏi danh sách và xóa displayOrder
        setPersonnelDisplayOrders((prevOrders) => {
          const newOrders = { ...prevOrders };
          delete newOrders[personnelId];
          return newOrders;
        });
        return prev.filter((id) => id !== personnelId);
      } else {
        // Nếu chọn thêm, tự động gắn thứ tự theo thứ tự chọn
        const newSelectedIds = [...prev, personnelId];
        setPersonnelDisplayOrders((prevOrders) => ({
          ...prevOrders,
          [personnelId]: newSelectedIds.length, // Tự động gắn thứ tự theo thứ tự chọn
        }));
        return newSelectedIds;
      }
    });
  }, []);

  const handlePersonnelCheckAll = useCallback(
    (checked) => {
      if (checked) {
        const allPersonnelIds = personnels.map((p) => p.personnelId);
        setSelectedPersonnelIds(allPersonnelIds);

        // Tự động gắn thứ tự cho tất cả personnel
        const autoDisplayOrders = {};
        allPersonnelIds.forEach((id, index) => {
          autoDisplayOrders[id] = index + 1;
        });
        setPersonnelDisplayOrders(autoDisplayOrders);
      } else {
        setSelectedPersonnelIds([]);
        setPersonnelDisplayOrders({});
      }
    },
    [personnels]
  );

  const handlePersonnelSearchChange = (e) => {
    setPersonnelSearch(e.target.value);
    setPersonnelPage(0);
  };

  const handleRemoveDocument = useCallback(
    async (idx, docId) => {
      if (isEditMode && docId) {
        try {
          // If we're in edit mode and this document has an ID, delete it from backend

          await deleteContractDocument(docId);

          // Show success message
          setSnackbar({
            open: true,
            message: "Đã xóa tài liệu thành công",
            severity: "success",
          });
        } catch (error) {
          console.error("Error deleting document:", error);
          setSnackbar({
            open: true,
            message: "Không thể xóa tài liệu. Vui lòng thử lại.",
            severity: "error",
          });
          return;
        }
      }
      // Remove from local state
      setDocuments((docs) => {
        const newDocs = docs.filter((_, i) => i !== idx);

        // Check if we need to adjust the current page
        const newTotalPages = Math.ceil(newDocs.length / documentsPerPage);
        if (documentsPage > newTotalPages && newTotalPages > 0) {
          setDocumentsPage(newTotalPages);
        } else if (newDocs.length === 0) {
          setDocumentsPage(1);
        }

        return newDocs;
      });
    },
    [isEditMode, documentsPerPage, documentsPage]
  );

  const handleDownloadDocument = useCallback(async (docId, documentName) => {
    try {
      await downloadContractDocument(docId, documentName);

      // Show success message
      setSnackbar({
        open: true,
        message: "Đang tải xuống tài liệu...",
        severity: "success",
      });
    } catch (error) {
      console.error("Error downloading document:", error);
      if (error.response) {
        console.error(
          "Error response:",
          error.response.status,
          error.response.data
        );
      }
      setSnackbar({
        open: true,
        message: "Không thể tải tài liệu. Vui lòng thử lại.",
        severity: "error",
      });
    }
  }, []);

  // Document table columns configuration
  const documentColumns = useMemo(
    () => [
      {
        accessorKey: "fileName",
        header: "Tên file",
        size: 200,
        Cell: ({ row }) => (
          <Typography variant="body2" sx={{ fontFamily: "Inter, sans-serif", fontSize: "12px" }}>
            {row.original.file?.name || row.original.fileName || "N/A"}
          </Typography>
        ),
      },
      {
        accessorKey: "documentType",
        header: "Loại file",
        size: 150,
        Cell: ({ row }) => (
          <Typography variant="body2" sx={{ fontFamily: "Inter, sans-serif", fontSize: "12px" }}>
            {row.original.documentType || "N/A"}
          </Typography>
        ),
      },
      {
        accessorKey: "description",
        header: "Mô tả",
        size: 200,
        Cell: ({ row }) => (
          <Typography variant="body2" sx={{ fontFamily: "Inter, sans-serif", fontSize: "12px" }}>
            {row.original.description || "Chưa có mô tả"}
          </Typography>
        ),
      },
      {
        id: "actions",
        header: "Hành động",
        size: 200,
        Cell: ({ row }) => {
          // Calculate the real index in the full documents array
          const realIndex = (documentsPage - 1) * documentsPerPage + row.index;

          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1,
              }}
            >
              {!row.original.id && (
                // <AppButton
                //   variant="outlined"
                //   onClick={() =>
                //     handlePreviewDocument(
                //       row.original,
                //       false
                //     )
                //   }
                //   disableElevation
                // >
                //   Xem trước
                // </AppButton>
                <Tooltip title="Xem trước">
                  <IconButton size="small"
                    onClick={() => handlePreviewDocument(
                      row.original,
                      false
                    )}>
                    <VisibilityOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {row.original.id && (
                <>
                  {/* <AppButton
                    variant="contained"
                    onClick={() =>
                      handlePreviewDocument(
                        row.original,
                        true
                      )
                    }
                    disableElevation
                  >
                    Xem trước
                  </AppButton> */}
                  <Tooltip title="Xem trước">
                    <IconButton size="small"
                      onClick={() => handlePreviewDocument(
                        row.original,
                        false
                      )}>
                      <VisibilityOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {/* <AppButton
                    variant="outlined"
                    onClick={() =>
                      handleDownloadDocument(
                        row.original.id,
                        row.original.file?.name
                      )
                    }
                    disableElevation
                  >
                    Tải về
                  </AppButton> */}
                  <Tooltip title="Tải xuống">
                    <IconButton size="small"
                      onClick={() =>
                        handleDownloadDocument(
                          row.original.id,
                          row.original.file?.name
                        )
                      }
                    >
                      <DownloadOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {/* <AppButton
                variant="outlined"
                color="error"
                onClick={() => {
                  handleRemoveDocument(realIndex, row.original.id);
                }}
                disableElevation
              >
                Xóa
              </AppButton> */}
              <Tooltip title="Xóa">
                <IconButton size="small" color="error"
                  onClick={() => {
                    handleRemoveDocument(realIndex, row.original.id);
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [
      documentsPage,
      documentsPerPage,
      handleDownloadDocument,
      handleRemoveDocument,
    ]
  );

  // Component for status change reason dialog
  const StatusChangeDialog = ({
    open,
    onClose,
    currentStatus,
    newStatus,
    onConfirm,
    error,
  }) => {
    const [reason, setReason] = useState("Chuyển trạng thái.");

    const handleConfirm = () => {
      onConfirm(reason);
    };

    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Nhập lý do thay đổi trạng thái</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Trạng thái hiện tại: <strong>{currentStatus}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Trạng thái mới: <strong>{newStatus}</strong>
            </Typography>
            <TextField
              label="Lý do thay đổi"
              fullWidth
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={!!error}
              helperText={error}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disableElevation>
            Hủy
          </Button>
          <Button onClick={handleConfirm} variant="contained" disableElevation>
            Xác nhận
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Component for selected personnel modal
  const SelectedPersonnelModal = ({ open, onClose }) => {
    const selectedPersonnelList = personnels
      .filter((p) => selectedPersonnelIds.includes(p.personnelId))
      .sort((a, b) => {
        const orderA = parseInt(personnelDisplayOrders[a.personnelId]) || 0;
        const orderB = parseInt(personnelDisplayOrders[b.personnelId]) || 0;
        return orderA - orderB;
      });

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Danh sách nhân sự được chọn ({selectedPersonnelIds.length} người)
        </DialogTitle>
        <DialogContent>
          {selectedPersonnelList.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "text.secondary",
              }}
            >
              <Typography variant="body1">
                Chưa có nhân sự nào được chọn
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden",
                mt: 1,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  px: 2,
                  py: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Grid container spacing={2}>
                  <Grid size={4}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Họ và tên
                    </Typography>
                  </Grid>
                  <Grid size={3}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Vai trò
                    </Typography>
                  </Grid>
                  <Grid size={2}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Thứ tự hiển thị
                    </Typography>
                  </Grid>
                  <Grid size={3}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Thao tác
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              {selectedPersonnelList.map((personnel) => (
                <Box
                  key={personnel.personnelId}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #e0e0e0",
                    "&:last-child": { borderBottom: "none" },
                    "&:hover": { backgroundColor: "#f9f9f9" },
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={4}>
                      <Typography variant="body2">
                        {personnel.fullName}
                      </Typography>
                    </Grid>
                    <Grid size={3}>
                      <Typography variant="body2">
                        {personnelRoles[personnel.personnelId] || "Thành viên"}
                      </Typography>
                    </Grid>
                    <Grid size={2}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "primary.main",
                        }}
                      >
                        {personnelDisplayOrders[personnel.personnelId] || 0}
                      </Typography>
                    </Grid>
                    <Grid size={3}>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          handlePersonnelCheck(personnel.personnelId);
                        }}
                        sx={{ textTransform: "none" }}
                        disableElevation
                      >
                        Xóa khỏi danh sách
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disableElevation>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const handleStatusChangeConfirm = (reason) => {
    // Update the form with the new status
    setForm((prev) => ({ ...prev, status: newStatus }));

    // Save the reason for when we submit the form
    setStatusChangeReason(reason);

    // Close the dialog
    setStatusDialogOpen(false);
  };

  const handleStatusChangeCancel = () => {
    // Close the dialog without changing status
    setStatusDialogOpen(false);
  };

  // Hàm upload document cho cả chế độ tạo mới và chỉnh sửa
  const uploadDocumentToServer = async (doc, contractId) => {
    try {
      // Kiểm tra đảm bảo doc.isNew === true được đặt cho tất cả các file mới
      if (doc.file instanceof File && !doc.id) {
        doc.isNew = true; // Always mark files with no ID but with a File object as new
      }

      // Trong chế độ chỉnh sửa, luôn tải lên file nếu đó là File object
      if (doc.file instanceof File) {
      }
      // Chỉ bỏ qua file đã tồn tại trên server và không phải file mới
      else if ((doc.id || doc.filePath) && !doc.isNew) {
        return null; // Bỏ qua file đã tồn tại trên server và không phải file mới
      }

      // Validate file
      if (!(doc.file instanceof File)) {
        return null;
      }

      const formData = new FormData();

      // Thêm contractId vào FormData - đảm bảo là chuỗi
      formData.append("ContractId", contractId.toString());

      // Thêm file vào form data với tên đầy đủ
      formData.append("File", doc.file, doc.file.name);

      // Thêm các trường bắt buộc khác
      formData.append("DocumentType", doc.documentType || "");
      formData.append("Description", doc.description || "");

      // Debug FormData content

      for (let pair of formData.entries()) {
        if (pair[0] === "File") {
        } else {
        }
      }

      const uploadResult = await uploadContractDocument(formData);

      return uploadResult;
    } catch (error) {
      console.error("DEBUG: Failed to upload document:", error);
      console.error(
        "DEBUG: Error details:",
        error.response?.data || error.message || error
      );
      throw error; // Ném lại lỗi để xử lý ở mức cao hơn
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? "Cập nhật hợp đồng" : "Thêm mới hợp đồng"}
      </DialogTitle>
      <DialogContent
        sx={{
          // minHeight: "78vh", // luôn giữ chiều cao tối thiểu lớn
          // maxHeight: "78vh", // không vượt quá viewport
          // overflowY: "auto",
          // overflowX: "visible", // Cho phép dropdown hiển thị ra ngoài theo chiều ngang
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {alert && <Alert severity={alert.type}>{alert.message}</Alert>}

        <AppTab
          value={tab}
          onChange={(_, value) => setTab(value)}
          tabs={tabs}
        />

        {/* Tab Content */}
        <Box sx={{ mt: 2 }}>
          <AppTabPanel value={tab} index={0}>
            <Grid container spacing={2}>
              {/* Hàng 1: Số hợp đồng, Tên hợp đồng */}
              <Grid size={3}>
                <AppTextField
                  label="Số hợp đồng"
                  name="contractNumber"
                  value={form.contractNumber}
                  onChange={(_, e) => handleChange(e)}
                  fullWidth
                  required
                  error={!!errors.contractNumber}
                  helperText={errors.contractNumber}
                />
              </Grid>
              <Grid size={9}>
                <AppTextField
                  label="Tên hợp đồng"
                  name="contractName"
                  value={form.contractName}
                  onChange={(_, e) => handleChange(e)}
                  fullWidth
                  required
                  error={!!errors.contractName}
                  helperText={errors.contractName}
                />
              </Grid>

              {/* Hàng 2: Chủ đầu tư */}
              <Grid size={12}>
                <AppAutocomplete
                  required
                  label="Chủ đầu tư"
                  value={form.clientId ?? ""}
                  options={clients.map((c) => ({ value: c.clientId, label: c.clientName }))}
                  onChange={(value) => {
                    setForm((prev) => ({ ...prev, clientId: value }));
                    setErrors((prev) => ({ ...prev, clientId: undefined }));
                  }}
                  error={!!errors.clientId}
                  helperText={errors.clientId}
                />
              </Grid>
              {/* Hàng 3: Đơn vị thi công */}
              <Grid size={12}>
                <AppAutocomplete
                  required
                  label="Đơn vị thi công"
                  value={form.contractorId ?? ""}
                  options={contractors.map((c) => ({ value: c.contractorId, label: c.contractorName }))}
                  onChange={(value) => {
                    setForm((prev) => ({ ...prev, contractorId: value }));
                    setErrors((prev) => ({ ...prev, contractorId: undefined }));
                  }}
                  error={!!errors.contractorId}
                  helperText={errors.contractorId}
                />
              </Grid>

              {/* Hàng 4: Ngày ký, Nhóm hợp đồng */}
              <Grid size={6}>
                <AppTextField
                  label="Ngày ký"
                  name="signDate"
                  type="date"
                  value={form.signDate}
                  onChange={(dateStr) => handleDateChange("signDate", dateStr)}
                  fullWidth
                  required
                  error={!!errors.signDate}
                  helperText={errors.signDate}
                />
              </Grid>
              <Grid size={6}>
                <AppAutocomplete
                  label="Nhóm hợp đồng"
                  value={form.contractGroup ?? ""}
                  options={contractGroups.map((g) => ({ value: g.name, label: g.name }))}
                  onChange={(value) => {
                    setForm((prev) => ({ ...prev, contractGroup: value }));
                    setErrors((prev) => ({ ...prev, contractGroup: undefined }));
                  }}
                  error={!!errors.contractGroup}
                  helperText={errors.contractGroup}
                />
              </Grid>

              {/* Hàng 5: Ngày bắt đầu, Ngày kết thúc */}
              <Grid size={6}>
                <AppTextField
                  label="Ngày bắt đầu"
                  name="startDate"
                  type="date"
                  value={form.startDate}
                  onChange={(dateStr) => handleDateChange("startDate", dateStr)}
                  fullWidth
                  required
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                />
              </Grid>
              <Grid size={6}>
                <AppTextField
                  label="Ngày kết thúc"
                  name="endDate"
                  type="date"
                  value={form.endDate}
                  onChange={(dateStr) => handleDateChange("endDate", dateStr)}
                  fullWidth
                  required
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                />
              </Grid>

              {/* Hàng 6: Ngày bắt đầu thực tế, Ngày kết thúc thực tế */}
              <Grid size={6}>
                <AppTextField
                  label="Ngày bắt đầu thực tế"
                  name="startDateAct"
                  type="date"
                  value={form.startDateAct}
                  onChange={(dateStr) => handleDateChange("startDateAct", dateStr)}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <AppTextField
                  label="Ngày kết thúc thực tế"
                  name="endDateAct"
                  type="date"
                  value={form.endDateAct}
                  onChange={(dateStr) => handleDateChange("endDateAct", dateStr)}
                  fullWidth
                />
              </Grid>

              {/* Hàng 7: Tổng giá trị hợp đồng, VAT */}
              <Grid size={9}>
                <AppTextField
                  label="Tổng giá trị hợp đồng"
                  name="totalValue"
                  value={form.totalValue}
                  onChange={(_, e) => handleTotalValueChange(e)}
                  fullWidth
                  required
                  error={!!errors.totalValue}
                  helperText={errors.totalValue}
                  placeholder="Nhập số tiền (VD: 1.000.000,50)"
                />
              </Grid>
              <Grid size={3}>
                <AppTextField
                  label="VAT (%)"
                  name="vat"
                  value={form.vat}
                  onChange={(_, e) => handleChange(e)}
                  fullWidth
                  type="number"
                  error={!!errors.vat}
                  helperText={errors.vat}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                />
              </Grid>

              {/* Hàng 8: Phạm vi, Trạng thái */}
              <Grid size={9}>
                <AppTextField
                  label="Phạm vi"
                  name="scope"
                  value={form.scope}
                  onChange={(_, e) => handleChange(e)}
                  fullWidth
                />
              </Grid>
              <Grid size={3}>
                <AppSelect
                  required
                  label="Trạng thái"
                  name="status"
                  value={form.status}
                  options={CONTRACT_STATUSES}
                  onChange={(value) => {
                    if (isEditMode) {
                      setInitialStatus(form.status);
                      setNewStatus(value);
                      setStatusChangeReason("");
                      setStatusChangeError("");
                      setStatusDialogOpen(true);
                      return;
                    }
                    setForm((prev) => ({ ...prev, status: value }));
                    setErrors((prev) => ({ ...prev, status: undefined }));
                  }}
                  error={!!errors.status}
                  helperText={errors.status}
                />

              </Grid>

              {/* Hàng 9: Mô tả hợp đồng */}
              <Grid size={12}>
                <AppTextField
                  label="Mô tả hợp đồng"
                  name="description"
                  value={form.description}
                  onChange={(_, e) => handleChange(e)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>

          </AppTabPanel>
          <AppTabPanel value={tab} index={1}>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                <CircularProgress />
              </Box>
            )}
            {!loading && documents.length === 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  mb: 2,
                  p: 2,
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body1" color="textSecondary">
                  Không có tài liệu đính kèm nào. Hãy tải lên tài liệu mới.
                </Typography>
              </Box>
            )}
            {/* {!loading && documents.length > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: "#e3f2fd", borderRadius: 1 }}>
              <Typography variant="body2" color="primary">
                Đã tải {documents.length} tài liệu
              </Typography>
            </Box>
          )} */}
            <Box mb={2} style={{ display: loading ? "none" : "block" }}>
              <Grid container spacing={2}>
                {/* Chọn file (12 cột) */}
                <Grid size={12}>
                  <FileDropzone
                    onDrop={(acceptedFiles) => {
                      if (acceptedFiles && acceptedFiles.length > 0) {
                        setDocUpload((prev) => ({
                          ...prev,
                          file: acceptedFiles[0],
                        }));
                      }
                    }}
                    file={docUpload.file}
                  />
                </Grid>
                {/* Loại tài liệu (6 cột) */}
                <Grid size={6}>
                  {/* <FormControl fullWidth>
                    <InputLabel>Loại tài liệu</InputLabel>
                    <Select
                      value={docUpload.documentType}
                      onChange={handleDocTypeChange}
                      label="Loại tài liệu"
                    >
                      {documentTypes.map((dt) => (
                        <MenuItem key={dt.documentTypeId} value={dt.name}>
                          {dt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl> */}
                  <AppSelect
                    label="Loại tài liệu"
                    name="documentType"
                    value={docUpload.documentType || ""}
                    options={documentTypes.map((d) => ({ value: d.name, label: d.name }))}
                    onChange={(value) => setDocUpload((prev) => ({ ...prev, documentType: value }))}
                  />

                </Grid>
                {/* Mô tả (6 cột) */}
                <Grid size={6}>
                  <AppTextField
                    label="Mô tả"
                    value={docUpload.description}
                    onChange={(value) => setDocUpload((prev) => ({ ...prev, description: value }))}
                  />
                </Grid>
                {/* Nút Thêm (12 cột, căn phải) */}
                <Grid size={12} sx={{ textAlign: "right" }}>
                  <AppButton
                    variant="contained"
                    onClick={handleAddDocument}
                    disableElevation
                  >
                    Thêm tài liệu
                  </AppButton>
                </Grid>
              </Grid>
            </Box>
            {/* Danh sách file đính kèm dạng MaterialReactTable */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Danh sách file đính kèm
              </Typography>

              {documents.length > 0 ? (
                <>
                  <DataTable
                    columns={documentColumns}
                    data={paginatedDocuments}
                    manualPagination={true}                 // 1. Báo cho DataTable biết dữ liệu đưa vào đã được phân trang sẵn
                    rowCount={documents.length}       // 2. Báo tổng số bản ghi thực tế để bảng tính đúng số trang
                    state={{
                      pagination: {
                        pageIndex: documentsPage - 1,       // 3. Trang hiện tại (0-based index)
                        pageSize: documentsPerPage,         // 4. Số dòng trên mỗi trang
                      },
                    }}
                    onPaginationChange={(updater) => {      // 5. Cập nhật lại state khi người dùng bấm chuyển trang / đổi số dòng
                      const nextPagination =
                        typeof updater === "function"
                          ? updater({
                            pageIndex: documentsPage - 1,
                            pageSize: documentsPerPage,
                          })
                          : updater;

                      setDocumentsPage(nextPagination.pageIndex + 1);
                      setDocumentsPerPage(nextPagination.pageSize);
                    }}
                    rowsPerPageOptions={[5, 10, 20]}
                  />
                </>

              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 100,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Chưa có file đính kèm
                  </Typography>
                </Box>
              )}
            </Box>
          </AppTabPanel>
          <AppTabPanel value={tab} index={2}>
            <Box mb={2}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Chọn nhân sự trong danh sách sau để thêm vào hợp đồng. Thứ tự chọn
                sẽ là thứ tự hiển thị trong hợp đồng.
              </Alert>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Box display="flex" alignItems="center">
                  <AppTextField
                    placeholder="Tìm kiếm nhân sự..."
                    value={personnelSearch}
                    onChange={(value) => setPersonnelSearch(value)}
                    sx={{ width: 400, mr: 2 }}
                  />

                  <Typography variant="body2" color="text.secondary">
                    Đã chọn: {selectedPersonnelIds.length} / {personnelTotal} nhân sự
                  </Typography>
                </Box>
                {selectedPersonnelIds.length > 0 && (
                  <AppButton
                    variant="outlined"
                    onClick={() => setSelectedPersonnelModalOpen(true)}
                    disableElevation
                  >
                    Xem danh sách được chọn ({selectedPersonnelIds.length})
                  </AppButton>
                )}
              </Box>

              <MaterialReactTable
                columns={useMemo(
                  () => [
                    {
                      id: "select",
                      header: "",
                      size: 50,
                      Cell: ({ row }) => (
                        <Checkbox
                          checked={selectedPersonnelIds.includes(
                            row.original.personnelId
                          )}
                          onChange={() =>
                            handlePersonnelCheck(row.original.personnelId)
                          }
                        />
                      ),
                      Header: ({ table }) => (
                        <Checkbox
                          indeterminate={
                            selectedPersonnelIds.length > 0 &&
                            selectedPersonnelIds.length < personnels.length
                          }
                          checked={
                            personnels.length > 0 &&
                            selectedPersonnelIds.length === personnels.length
                          }
                          onChange={(e) =>
                            handlePersonnelCheckAll(e.target.checked)
                          }
                        />
                      ),
                      enableSorting: false,
                      enableColumnFilter: false,
                    },
                    {
                      accessorKey: "displayOrder",
                      header: "STT",
                      size: 60,
                      Cell: ({ cell }) => (
                        <Typography variant="body2" align="center">
                          {cell.getValue() || 0}
                        </Typography>
                      ),
                    },
                    {
                      accessorKey: "fullName",
                      header: "Họ và tên",
                      size: 180,
                      Cell: ({ cell }) => (
                        <Typography variant="body2">{cell.getValue()}</Typography>
                      ),
                    },
                    {
                      accessorKey: "title",
                      header: "Chức vụ",
                      size: 140,
                      Cell: ({ cell }) => cell.getValue() || "N/A",
                    },
                    {
                      accessorKey: "departmentName",
                      header: "Phòng ban",
                      size: 160,
                      Cell: ({ cell }) => cell.getValue() || "N/A",
                    },
                    {
                      accessorKey: "enterpriseName",
                      header: "Xí nghiệp",
                      size: 120,
                      Cell: ({ cell }) => (
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            lineHeight: 1.2,
                          }}
                        >
                          {cell.getValue() || "N/A"}
                        </Typography>
                      ),
                    },
                    {
                      id: "role",
                      header: "Vai trò trong dự án",
                      size: 180,
                      columnDefType: "display",
                      Cell: ({ row }) => (
                        <Select
                          size="small"
                          value={
                            personnelRoles[row.original.personnelId] ||
                            "Thành viên"
                          }
                          onChange={(e) => {
                            // When changing role, make sure the person is also selected
                            if (
                              !selectedPersonnelIds.includes(
                                row.original.personnelId
                              )
                            ) {
                              setSelectedPersonnelIds((prev) => {
                                const newSelected = [
                                  ...prev,
                                  row.original.personnelId,
                                ];
                                // Tự động gắn displayOrder khi chọn qua role
                                setPersonnelDisplayOrders((prevOrders) => ({
                                  ...prevOrders,
                                  [row.original.personnelId]: newSelected.length,
                                }));
                                return newSelected;
                              });
                            }
                            // Update their role
                            setPersonnelRoles((r) => ({
                              ...r,
                              [row.original.personnelId]: e.target.value,
                            }));
                          }}
                          sx={{ minWidth: 120 }}
                          disabled={
                            !selectedPersonnelIds.includes(
                              row.original.personnelId
                            )
                          }
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 200,
                              },
                            },
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left",
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                            getContentAnchorEl: null,
                          }}
                        >
                          <MenuItem value="Thành viên">Thành viên</MenuItem>
                          {roleOptions
                            .filter((r) => r !== "Thành viên")
                            .map((role) => (
                              <MenuItem key={role} value={role}>
                                {role}
                              </MenuItem>
                            ))}
                        </Select>
                      ),
                      enableSorting: false,
                      enableColumnFilter: false,
                    },
                  ],
                  [
                    selectedPersonnelIds,
                    personnels.length,
                    personnelRoles,
                    roleOptions,
                    handlePersonnelCheck,
                    handlePersonnelCheckAll,
                  ]
                )}
                data={personnels}
                enableRowSelection={false}
                enableColumnActions={false}
                enableGlobalFilter={false}
                enableFilters={false}
                enablePagination={true}
                enableDensityToggle={false}
                enableHiding={false}
                enableFullScreenToggle={false}
                enableTopToolbar={false}
                enableColumnPinning={true}
                manualPagination={true}
                pageCount={Math.ceil(personnelTotal / personnelPageSize)}
                rowCount={personnelTotal}
                localization={MRT_Localization_VI}
                onPaginationChange={(updater) => {
                  if (typeof updater === "function") {
                    const newState = updater({
                      pageIndex: personnelPage,
                      pageSize: personnelPageSize,
                    });
                    setPersonnelPage(newState.pageIndex);
                    setPersonnelPageSize(newState.pageSize);
                  } else {
                    setPersonnelPage(updater.pageIndex);
                    setPersonnelPageSize(updater.pageSize);
                  }
                }}
                initialState={{
                  density: "compact",
                  columnPinning: {
                    right: ["role"],
                  },
                }}
                state={{
                  pagination: {
                    pageIndex: personnelPage,
                    pageSize: personnelPageSize,
                  },
                  isLoading: personnelLoading,
                }}
                muiTablePaperProps={{
                  sx: {
                    boxShadow: "none",
                    border: "1px solid #e0e0e0",
                  },
                }}
                muiTableHeadCellProps={{
                  sx: {
                    backgroundColor: "#f5f5f5",
                    border: "0.5px solid #e0e0e0",
                    fontWeight: 600,
                  },
                }}
                muiTableBodyCellProps={{
                  sx: {
                    border: "0.5px solid #e0e0e0",
                  },
                }}
                muiTableBodyRowProps={({ row }) => ({
                  hover: true,
                  sx: {
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  },
                })}
                muiPaginationProps={{
                  rowsPerPageOptions: [5, 10, 20, 50],
                  labelRowsPerPage: "Số dòng/trang:",
                  labelDisplayedRows: ({ from, to, count }) =>
                    `${from}-${to} của ${count}`,
                  showFirstButton: true,
                  showLastButton: true,
                }}
                renderEmptyRowsFallback={() => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 200,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {personnelLoading ? "Đang tải..." : "Không có nhân sự"}
                    </Typography>
                  </Box>
                )}
              />
            </Box>
          </AppTabPanel>
        </Box>
      </DialogContent>

      <DialogActions>
        <AppButton variant="outlined" onClick={onClose} disabled={submitting} disableElevation>
          Hủy
        </AppButton>
        <AppButton
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          disableElevation
        >
          {isEditMode ? "Cập nhật hợp đồng" : "Tạo hợp đồng"}
        </AppButton>
      </DialogActions>
      {/* Status Change Dialog */}
      <StatusChangeDialog
        open={statusDialogOpen}
        onClose={handleStatusChangeCancel}
        currentStatus={initialStatus}
        newStatus={newStatus}
        onConfirm={handleStatusChangeConfirm}
        error={statusChangeError}
      />

      {/* Selected Personnel Modal */}
      <SelectedPersonnelModal
        open={selectedPersonnelModalOpen}
        onClose={() => setSelectedPersonnelModalOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Dialog>
  );
};

export default AddContractModal;
